import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FileText, 
  Inbox, 
  Home, 
  User, 
  Plus, 
  Folder,
  Layout,
  FileCheck,
  Send,
  Loader2,
  File
} from 'lucide-react';
import { useCommandStore } from '../../store/useCommandStore';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator
} from '../ui/Command';
import { useSettings } from '../../context/SettingsContext';

const CommandPalette = () => {
  const { isOpen, setOpen, toggle } = useCommandStore();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [toggle]);

  useEffect(() => {
    if (!query || query.length < 3) {
        setResults([]);
        return;
    }

    const timer = setTimeout(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`/api/search?q=${query}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                const data = Array.isArray(response.data.data) ? response.data.data : [];
                setResults(isEfileEnabled ? data : data.filter(item => item.category !== 'File'));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const runCommand = (command) => {
    setOpen(false);
    command();
  };

  const handleSelect = (item) => {
      runCommand(() => {
          if (item.category === 'Dispatch') navigate(`/dispatch/${item.id}/view`);
          else if (item.category === 'Receipt') navigate(`/diary/${item.id}/view`);
          else if (item.category === 'File') navigate(`/efile/${item.id}`);
          else if (item.category === 'User') navigate(`/users?search=${item.name}`);
      });
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen} commandProps={{ shouldFilter: false }}>
      <CommandInput 
        placeholder="Type a command or search..." 
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
            {loading ? (
                <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
            ) : (
                query.length < 3 ? 'Type at least 3 characters to search...' : 'No results found.'
            )}
        </CommandEmpty>
        
        {results.length > 0 && (
            <>
                {['Dispatch', 'Receipt', ...(isEfileEnabled ? ['File'] : []), 'User'].map(category => {
                    const categoryItems = results.filter(item => item.category === category);
                    if (categoryItems.length === 0) return null;
                    
                    return (
                        <CommandGroup heading={`${category}s`} key={category}>
                            {categoryItems.map((item) => (
                                <CommandItem
                                    key={`${item.category}-${item.id}`}
                                    onSelect={() => handleSelect(item)}
                                    value={`${item.category} ${item.subject || item.name || item.number}`}
                                >
                                    {item.category === 'File' && <Folder className="mr-2 h-4 w-4 text-indigo-500" />}
                                    {item.category === 'User' && <User className="mr-2 h-4 w-4 text-emerald-500" />}
                                    {item.category === 'Dispatch' && <Send className="mr-2 h-4 w-4 text-blue-500" />}
                                    {item.category === 'Receipt' && <Inbox className="mr-2 h-4 w-4 text-orange-500" />}
                                    
                                    <div className="flex flex-col">
                                        <span className="font-medium">{item.subject || item.name || item.number}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {item.number || item.email || item.status}
                                        </span>
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    );
                })}
            </>
        )}
        
        <CommandSeparator />

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => runCommand(() => navigate('/dashboard'))}>
            <Home className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
          {isEfileEnabled && (
            <>
              <CommandItem onSelect={() => runCommand(() => navigate('/files'))}>
                <Folder className="mr-2 h-4 w-4" />
                <span>File Workspace</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/kanban'))}>
                <Layout className="mr-2 h-4 w-4" />
                <span>My Desk</span>
              </CommandItem>
              <CommandItem onSelect={() => runCommand(() => navigate('/approvals'))}>
                <FileCheck className="mr-2 h-4 w-4" />
                <span>Approvals</span>
              </CommandItem>
            </>
          )}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => runCommand(() => navigate('/dispatch/new'))}>
            <Plus className="mr-2 h-4 w-4" />
            <span>New Dispatch</span>
          </CommandItem>
          <CommandItem onSelect={() => runCommand(() => navigate('/diary/new'))}>
            <Plus className="mr-2 h-4 w-4" />
            <span>New Receipt</span>
          </CommandItem>
        </CommandGroup>

      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
